import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, Archive, Download, FileText, MoreVertical, Search, Trash2, Upload } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, Cell, Line, LineChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import AppShell from "../components/AppShell";
import { api } from "../lib/api";

const money = new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 0 });
const CATEGORIES = ["Office Supplies", "Fuel", "Travel", "Marketing", "Software", "Payroll", "Professional Services", "Utilities", "Equipment", "Repairs", "Rent", "Insurance", "Taxes", "Other"];
const STATUS_META = {
  pending: { label: "Pendiente", color: "#a97a11", bg: "#fff2c9" },
  paid: { label: "Pagada", color: "#238a65", bg: "#dcf7ed" },
  cancelled: { label: "Cancelada", color: "#d7565d", bg: "#ffe5e6" },
  deductible: { label: "Deducible", color: "#4169e1", bg: "#eef4ff" },
};
const PIE_COLORS = ["#4169e1", "#28a878", "#f7c34e", "#ef6670", "#7c3aed", "#0ea5e9", "#f97316", "#84cc16"];

export default function ReceivedInvoicesPage() {
  const navigate = useNavigate();
  const [docs, setDocs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("");
  const [status, setStatus] = useState("");
  const [category, setCategory] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [importSummary, setImportSummary] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const [listRes, sumRes] = await Promise.all([
        api.get("/invoices/received", { params: { q, filter, status, category }, withCredentials: true }),
        api.get("/invoices/received/summary", { withCredentials: true }),
      ]);
      setDocs(listRes.data);
      setSummary(sumRes.data);
    } catch (issue) { setError(issue.response?.data?.detail || "No pudimos cargar."); }
  }, [q, filter, status, category]);

  useEffect(() => { const t = setTimeout(refresh, 200); return () => clearTimeout(t); }, [refresh]);

  const upload = async (files) => {
    const list = files?.length ? Array.from(files) : [];
    if (!list.length) return;
    const body = new FormData();
    list.forEach((f) => body.append("files", f));
    setUploading(true);
    setImportSummary(null);
    try {
      const r = await api.post("/invoices/received/upload", body, { withCredentials: true });
      const d = r.data || {};
      setImportSummary({
        imported: d.imported || 0,
        suppliers_created: d.suppliers_created || 0,
        deductible: d.deductible || 0,
        with_issues: d.with_issues || 0,
        skipped: d.skipped || [],
      });
      const bits = [`Importadas ${d.imported || 0}`];
      if (d.suppliers_created) bits.push(`${d.suppliers_created} nuevos proveedores`);
      if (d.deductible) bits.push(`${d.deductible} marcadas deducibles`);
      if (d.with_issues) bits.push(`${d.with_issues} con avisos`);
      setNotice(bits.join(" · "));
      refresh();
    } catch (issue) { setError(issue.response?.data?.detail || "No pudimos importar."); }
    finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const onDrop = (e) => { e.preventDefault(); setDragOver(false); upload(e.dataTransfer.files); };
  const toggleSelect = (id) => setSelected((c) => { const n = new Set(c); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const clearSel = () => setSelected(new Set());

  const bulk = async (action, extra = {}) => {
    if (!selected.size) return;
    try {
      const r = await api.post("/invoices/received/bulk-action", { ids: [...selected], action, ...extra }, { withCredentials: true });
      setNotice(`Aplicado a ${Object.values(r.data)[0]} factura(s).`);
      clearSel(); refresh();
    } catch (issue) { setError(issue.response?.data?.detail || "No pudimos."); }
  };

  const changeCategory = async (doc, newCat) => {
    try {
      await api.put(`/invoices/received/${doc.id}`, { category: newCat }, { withCredentials: true });
      refresh();
    } catch (issue) { setError(issue.response?.data?.detail || "No pudimos."); }
  };

  const kpis = summary || { total_expenses: 0, deductible_expenses: 0, iva_credit: 0, count: 0, top_supplier: "—", average_monthly: 0 };

  return (
    <AppShell>
      <main className="page invoices-received-page"
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
      >
        <header className="expenses-head">
          <div>
            <small className="eyebrow">FACTURAS RECIBIDAS</small>
            <h1>Tu ledger de <em>compras.</em></h1>
            <p style={{ color: "#5a666b", margin: "6px 0 0" }}>Arrastra 1 archivo, muchos XML+PDF, o un ZIP con carpeta de proveedores. Detectamos proveedores nuevos y marcamos las que son deducibles.</p>
          </div>
          <div className="expenses-head-actions">
            <button className="expenses-add-button" data-testid="received-upload-btn" onClick={() => fileInput.current?.click()} disabled={uploading}>
              <Upload size={17} />
              {uploading ? "Importando..." : "Subir facturas"}
            </button>
            <input ref={fileInput} type="file" multiple accept=".xml,.pdf,.zip,application/xml,application/pdf,application/zip" style={{ display: "none" }} data-testid="received-file-input" onChange={(e) => upload(e.target.files)} />
          </div>
        </header>

        {dragOver && <div className="received-drop-hint">Suelta para importar</div>}
        {notice && <p className="expenses-notice" data-testid="received-notice">{notice}</p>}
        {error && <p className="expenses-error">{error}</p>}

        {importSummary && (
          <section className="received-import-summary" data-testid="received-import-summary">
            <header>
              <b>Última importación</b>
              <button type="button" className="link" onClick={() => setImportSummary(null)} aria-label="Cerrar">×</button>
            </header>
            <div className="received-import-grid">
              <article><small>Importadas</small><strong>{importSummary.imported}</strong></article>
              <article className="ok"><small>Deducibles auto</small><strong>{importSummary.deductible}</strong></article>
              <article className="info"><small>Proveedores nuevos</small><strong>{importSummary.suppliers_created}</strong></article>
              <article className={importSummary.with_issues ? "warn" : ""}><small>Con avisos</small><strong>{importSummary.with_issues}</strong></article>
            </div>
            {importSummary.skipped.length > 0 && (
              <ul className="received-import-skipped">
                {importSummary.skipped.map((s, i) => (
                  <li key={i} data-testid={`received-skipped-${i}`}>⚠️ {s.file}: {s.reason}</li>
                ))}
              </ul>
            )}
          </section>
        )}

        <section className="expenses-summary" data-testid="received-kpis">
          <article><small>TOTAL EGRESOS</small><strong>{money.format(kpis.total_expenses)}</strong></article>
          <article><small>DEDUCIBLES</small><strong style={{ color: "#238a65" }}>{money.format(kpis.deductible_expenses)}</strong></article>
          <article><small>IVA ACREDITABLE</small><strong style={{ color: "#4169e1" }}>{money.format(kpis.iva_credit)}</strong></article>
          <article><small>FACTURAS</small><strong>{kpis.count}</strong></article>
          <article><small>MAYOR PROVEEDOR</small><strong style={{ fontSize: 16 }}>{kpis.top_supplier}</strong><span>Prom. mensual: {money.format(kpis.average_monthly)}</span></article>
        </section>

        <section className="sent-charts">
          <article data-testid="chart-monthly">
            <b>Egresos mensuales</b>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={summary?.monthly || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f2f4" />
                <XAxis dataKey="month" fontSize={11} />
                <YAxis fontSize={11} />
                <Tooltip formatter={(v) => money.format(v)} />
                <Line type="monotone" dataKey="total" stroke="#ef6670" strokeWidth={2} dot />
              </LineChart>
            </ResponsiveContainer>
          </article>
          <article data-testid="chart-by-category">
            <b>Por categoría</b>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={summary?.by_category || []} innerRadius={40} outerRadius={70} paddingAngle={2} dataKey="total" nameKey="name">
                  {(summary?.by_category || []).map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => money.format(v)} />
              </PieChart>
            </ResponsiveContainer>
          </article>
          <article data-testid="chart-top-suppliers">
            <b>Top proveedores</b>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={summary?.top_suppliers || []} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f2f4" />
                <XAxis type="number" fontSize={11} />
                <YAxis dataKey="name" type="category" fontSize={11} width={100} />
                <Tooltip formatter={(v) => money.format(v)} />
                <Bar dataKey="total" fill="#4169e1" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </article>
          <article data-testid="chart-cash-outflow">
            <b>Tendencia de salida</b>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={summary?.monthly || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f2f4" />
                <XAxis dataKey="month" fontSize={11} />
                <YAxis fontSize={11} />
                <Tooltip formatter={(v) => money.format(v)} />
                <Bar dataKey="total" fill="#28a878" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </article>
        </section>

        <section className="expenses-controls">
          <label className="expenses-search">
            <Search size={17} />
            <input data-testid="received-search" placeholder="Buscar por proveedor, RFC, UUID, monto..." value={q} onChange={(e) => setQ(e.target.value)} />
          </label>
          <div className="expenses-period">
            <button className={filter === "" ? "active" : ""} onClick={() => setFilter("")}>Todas</button>
            <button className={filter === "month" ? "active" : ""} onClick={() => setFilter("month")}>Mes</button>
            <button className={filter === "year" ? "active" : ""} onClick={() => setFilter("year")}>Año</button>
          </div>
          <select className="expenses-select" value={category} onChange={(e) => setCategory(e.target.value)} data-testid="received-category-filter">
            <option value="">Cualquier categoría</option>
            {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </select>
          <select className="expenses-select" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="received-status-filter">
            <option value="">Cualquier estado</option>
            <option value="pending">Pendientes</option>
            <option value="paid">Pagadas</option>
            <option value="deductible">Deducibles</option>
            <option value="cancelled">Canceladas</option>
          </select>
        </section>

        {selected.size > 0 && (
          <div className="vault-bulk-bar">
            <b>{selected.size} seleccionada{selected.size === 1 ? "" : "s"}</b>
            <button onClick={() => bulk("archive")}><Archive size={14} />Archivar</button>
            <button onClick={() => { const c = window.prompt("Cambiar categoría a:", "Software"); if (c && CATEGORIES.includes(c)) bulk("change_category", { category: c }); }}>Cambiar categoría</button>
            <button onClick={() => bulk("delete")} className="danger"><Trash2 size={14} />Eliminar</button>
            <button onClick={clearSel}>Cancelar</button>
          </div>
        )}

        {docs.length === 0 ? (
          <div className="expenses-empty" data-testid="received-empty">
            <span><Upload size={22} /></span>
            <h2>Aún no importas facturas recibidas.</h2>
            <p>Arrastra archivos aquí o usa el botón para importar XML/PDF/ZIP.</p>
          </div>
        ) : (
          <div className="expenses-table-wrap">
            <table className="expenses-table" data-testid="received-table">
              <thead>
                <tr>
                  <th style={{ width: 40 }}></th>
                  <th>Proveedor</th>
                  <th>Fecha</th>
                  <th>Total</th>
                  <th>IVA</th>
                  <th>Categoría</th>
                  <th>Estado</th>
                  <th>PDF</th>
                  <th>XML</th>
                  <th>Alertas</th>
                </tr>
              </thead>
              <tbody>
                {docs.map((d) => {
                  const meta = STATUS_META[d.status] || STATUS_META.pending;
                  return (
                    <tr key={d.id} data-testid={`received-row-${d.id}`}>
                      <td><input type="checkbox" checked={selected.has(d.id)} onChange={() => toggleSelect(d.id)} /></td>
                      <td onClick={() => navigate(`/facturas-recibidas/${d.id}`)} style={{ cursor: "pointer" }}>
                        <b>{d.cfdi?.supplier_name || "Sin nombre"}</b>
                        <small>{d.cfdi?.supplier_rfc || "—"}</small>
                      </td>
                      <td>{d.cfdi?.invoice_date || (d.created_at || "").slice(0, 10)}</td>
                      <td><strong>{money.format(Number(d.cfdi?.total || 0))}</strong></td>
                      <td>{money.format(Number(d.cfdi?.iva || 0))}</td>
                      <td>
                        <select value={d.category} onChange={(e) => changeCategory(d, e.target.value)} data-testid={`received-cat-${d.id}`} style={{ padding: "4px 8px", borderRadius: 8, border: "1px solid var(--line)", fontSize: 12, fontWeight: 700 }}>
                          {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                        </select>
                      </td>
                      <td><span className="sent-status" style={{ background: meta.bg, color: meta.color }}>{meta.label}</span></td>
                      <td>{d.pdf_path ? "✓" : "—"}</td>
                      <td>{d.xml_path ? "✓" : "—"}</td>
                      <td>{d.issues?.length > 0 && <span title={d.issues.join(", ")} style={{ color: "#d7565d", display: "inline-flex", alignItems: "center", gap: 4 }}><AlertTriangle size={14} />{d.issues.length}</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </AppShell>
  );
}
